<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'
import ReportParametersDialog from './ReportParametersDialog.vue'
import { getApiError } from '@/composables/getApiError'
import { archiveReportDraft, getReportDraft, listReportDrafts } from '@/modules/reportTemplates/api'
import type { ReportDraft, ReportDraftSummary } from '@/modules/reportTemplates/api'

const props = defineProps<{ objectType?: string; objectId?: string | number; refreshKey?: number }>()
const records = ref<ReportDraftSummary[]>([])
const archived = ref(false)
const page = ref(1)
const count = ref(0)
const busy = ref(false)
const error = ref('')
const editing = ref<{ draft: ReportDraft; copy: boolean } | null>(null)
let request: AbortController | undefined
function controller() {
  request?.abort()
  request = new AbortController()
  return request
}
async function load() {
  const ctrl = controller()
  busy.value = true
  error.value = ''
  try {
    const data = await listReportDrafts({ page: page.value, archived: archived.value, object_type: props.objectType, object_id: props.objectId }, ctrl.signal)
    if (!ctrl.signal.aborted) {
      records.value = data.results
      count.value = data.count
    }
  } catch (cause) {
    if (!ctrl.signal.aborted) error.value = getApiError(cause, 'Не удалось загрузить документы')
  } finally {
    if (!ctrl.signal.aborted) busy.value = false
  }
}
async function open(record: ReportDraftSummary, copy = false) {
  const ctrl = controller()
  busy.value = true
  error.value = ''
  try {
    const draft = await getReportDraft(record.id, ctrl.signal)
    if (!ctrl.signal.aborted) editing.value = { draft, copy }
  } catch (cause) {
    if (!ctrl.signal.aborted) error.value = getApiError(cause, 'Не удалось открыть документ')
  } finally {
    if (!ctrl.signal.aborted) busy.value = false
  }
}
async function archive(record: ReportDraftSummary) {
  const ctrl = controller()
  busy.value = true
  error.value = ''
  try {
    await archiveReportDraft(record, ctrl.signal)
    if (!ctrl.signal.aborted) {
      if (records.value.length === 1 && page.value > 1) page.value--
      else await load()
    }
  } catch (cause) {
    if (!ctrl.signal.aborted) error.value = getApiError(cause, 'Не удалось изменить архив')
  } finally {
    if (!ctrl.signal.aborted) busy.value = false
  }
}
const date = (value: string) => new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
watch(archived, () => {
  if (page.value !== 1) page.value = 1
  else load()
})
watch(() => [page.value, props.objectType, props.objectId, props.refreshKey], load, { immediate: true })
onBeforeUnmount(() => request?.abort())
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between flex-wrap gap-2 mb-4">
      <h3 class="text-h6">
        Сохранённые документы
      </h3>
      <div class="d-flex align-center gap-2">
        <VSwitch
          v-model="archived"
          label="Архив"
          hide-details
          density="compact"
          :disabled="busy"
        />
        <VBtn
          icon="ri-refresh-line"
          variant="text"
          aria-label="Обновить документы"
          :disabled="busy"
          @click="load"
        />
      </div>
    </div>
    <p class="text-body-2 text-medium-emphasis mb-4">
      Здесь только ваши черновики и параметры сформированных документов. Копию можно использовать для нового заявления. Сохранение не отправляет документ на согласование.
    </p>
    <VProgressLinear v-if="busy" indeterminate />
    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-3"
    >
      {{ error }}
    </VAlert>
    <p v-if="!busy && !error && !records.length" class="py-4">
      {{ archived ? 'В архиве пока нет документов.' : 'Сохранённых документов пока нет. Выберите форму и сохраните черновик.' }}
    </p>
    <VCard
      v-for="record in records"
      :key="record.id"
      variant="outlined"
      class="pa-4 mb-3"
    >
      <div class="text-subtitle-1 text-wrap">
        {{ record.title }}
      </div>
      <div class="text-body-2 text-medium-emphasis">
        {{ record.template_name }} · {{ record.format.toUpperCase() }}
      </div>
      <div class="text-caption mt-1">
        Изменён: {{ date(record.updated_at) }} · {{ record.generation_count ? `Сформирован ${record.generation_count} раз` : 'Черновик' }}
      </div>
      <div class="d-flex flex-wrap gap-2 mt-3">
        <VBtn
          v-if="!record.is_archived"
          size="small"
          variant="tonal"
          :disabled="busy"
          @click="open(record)"
        >
          Продолжить
        </VBtn>
        <VBtn
          size="small"
          variant="text"
          :disabled="busy"
          @click="open(record, true)"
        >
          Создать копию
        </VBtn>
        <VBtn
          size="small"
          variant="text"
          :disabled="busy"
          @click="archive(record)"
        >
          {{ record.is_archived ? 'Восстановить' : 'В архив' }}
        </VBtn>
      </div>
    </VCard>
    <VPagination
      v-if="count > 20"
      v-model="page"
      :length="Math.ceil(count / 20)"
      :disabled="busy"
      :total-visible="5"
    />
    <ReportParametersDialog
      v-if="editing"
      :key="`${editing.draft.id}:${editing.copy}`"
      :template="editing.draft.template"
      :source="editing.draft.source"
      :format="editing.draft.format"
      :draft="editing.draft"
      :copy="editing.copy"
      @saved="load"
      @close="editing = null"
    />
  </section>
</template>
